import { ELanguage } from '@if/core/types'
import { formatCurrencyNoDecimals } from '@if/core/utils'
import * as yup from '@if/core/yup'
import { ActionButton, type ColumnDef } from '@if/mui'
import { useInvitationHistory } from '@src/data/api/invitation-api/invitation-api'
import {
  EInvitationDeliveryStatus,
  InvitationHistoryEntryDto,
  InvitationHistoryFilterDto,
} from '@src/types/InvitationHistoryDto'
import { ECommunicationType } from '@src/types/InvitationSchema'
import { RotateCcw } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { hasResolvableChannel, InvitationHistoryRow, toChannelRows } from './invitationHistoryRows'

export const invitationHistoryFilterSchema = yup.object({
  // A banner reads one subsidiary's history; null (not '') when unset, so the Autocomplete's empty
  // value is valid. Standard users leave it null (merchant from the token).
  merchantId: yup.text().nullable().default(null),
  // One box for the recipient. The four fields it stands in for are still on the endpoint, but they
  // AND together on the credit side, so asking the user which one to type in was the wrong question.
  search: yup.text().default(''),
  communicationType: yup.text().default(''),
  deliveryStatus: yup.text().default(''),
  sentByUserId: yup.text().default(''),
  sentFrom: yup.dateOnly().nullable().default(null),
  sentTo: yup.dateOnly().nullable().default(null),
})

export type InvitationHistoryFilters = yup.InferSubmit<typeof invitationHistoryFilterSchema>

export type InvitationHistoryFilterDraft = yup.InferDraft<typeof invitationHistoryFilterSchema>

export type InvitationHistoryQuery = InvitationHistoryFilterDto & { offset: number; limit: number }

export const toHistoryQuery = (
  filters: InvitationHistoryFilters,
  pagination: { offset: number; limit: number },
): InvitationHistoryQuery => ({
  merchantId: filters.merchantId || null,
  search: filters.search || null,
  communicationType: (filters.communicationType as ECommunicationType) || null,
  deliveryStatus: (filters.deliveryStatus as EInvitationDeliveryStatus) || null,
  sentByUserId: filters.sentByUserId || null,
  sentFrom: filters.sentFrom?.toString() ?? null,
  sentTo: filters.sentTo?.toString() ?? null,
  offset: pagination.offset,
  limit: pagination.limit,
})

export const isSameHistoryFilters = (a: InvitationHistoryFilters, b: InvitationHistoryFilters): boolean => {
  const unpaged = { offset: 0, limit: 0 }

  return JSON.stringify(toHistoryQuery(a, unpaged)) === JSON.stringify(toHistoryQuery(b, unpaged))
}

export const hasHistoryFilters = (query: InvitationHistoryFilterDto): boolean =>
  // merchantId is a banner's chosen subsidiary — a gate, not a filter
  Object.entries(query).some(
    ([key, value]) => key !== 'offset' && key !== 'limit' && key !== 'merchantId' && value !== null,
  )

export const useInvitationHistoryData = (query: InvitationHistoryQuery, enabled = true) => {
  const [shouldPoll, setShouldPoll] = useState(false)
  const { data, isLoading, isError } = useInvitationHistory({ ...query, limit: query.limit + 1 }, enabled, shouldPoll)
  const fetched = useMemo(() => data ?? [], [data])

  useEffect(() => {
    setShouldPoll(hasResolvableChannel(data, Date.now()))
  }, [data])

  const invitations = useMemo(() => fetched.slice(0, query.limit), [fetched, query.limit])
  const rows = useMemo(
    () => toChannelRows(invitations, query.communicationType),
    [invitations, query.communicationType],
  )

  return {
    invitations,
    pageCount: invitations.length,
    hasMore: fetched.length > query.limit,
    rows,
    isLoading,
    isError,
  }
}

export const useHistorySenders = (invitations: InvitationHistoryEntryDto[]) =>
  useMemo(() => {
    const byId = new Map<string, string>()

    invitations.forEach((entry) => {
      if (entry.sentByUserId) {
        byId.set(entry.sentByUserId, entry.sentByUserFullname ?? entry.sentByUserId)
      }
    })

    return [...byId.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [invitations])

export const useInvitationHistoryColumns = (
  onResend: (entry: InvitationHistoryEntryDto) => void,
  resendDisabled = false,
) => {
  const { t } = useTranslation()

  return useMemo<ColumnDef<InvitationHistoryRow>[]>(
    () => [
      {
        name: 'communicationType',
        type: 'enumChip',
        set: ECommunicationType,
        header: t('invitationHistory.columns.type'),
        minWidth: 120,
      },
      {
        name: 'recipientName',
        type: 'titleSubtitle',
        header: t('invitationHistory.columns.recipient'),
        minWidth: 180,
        flexGrow: 2,
        getValue: (row) =>
          `${row.firstName ?? ''} ${row.lastName ?? ''}${row.isResend ? ` (${t('invitationHistory.resent')})` : ''}`.trim(),
        getSubtitle: (row) => row.recipient,
      },
      {
        name: 'languageId',
        header: t('invitationHistory.columns.language'),
        align: 'center',
        minWidth: 80,
        getFormatted: (_value, row) => ELanguage[row.languageId] ?? '',
      },
      {
        name: 'amount',
        header: t('invitationHistory.columns.amount'),
        align: 'right',
        minWidth: 100,
        getFormatted: (_value, row) => (row.amount ? formatCurrencyNoDecimals(row.amount) : ''),
      },
      {
        name: 'sentAt',
        type: 'date',
        header: t('invitationHistory.columns.sentAt'),
        minWidth: 160,
      },
      {
        name: 'sentByUserFullname',
        header: t('invitationHistory.columns.sentBy'),
        minWidth: 140,
        flexGrow: 1,
      },
      {
        name: 'deliveryStatus',
        type: 'enumChip',
        set: EInvitationDeliveryStatus,
        header: t('invitationHistory.columns.status'),
        minWidth: 130,
        emptyPlaceholder: t('invitationHistory.status.unknown'),
      },
      {
        name: 'resend',
        header: t('invitationHistory.columns.actions'),
        minWidth: 130,
        getValue: () => '',
        renderCell: (row) => (
          <ActionButton
            size="small"
            startIcon={RotateCcw}
            disabled={resendDisabled}
            onClick={() => onResend(row.entry)}
          >
            {t('invitationHistory.resend')}
          </ActionButton>
        ),
      },
    ],
    [t, onResend, resendDisabled],
  )
}
